import { useState, useEffect } from 'react';
import { combineStyles } from 'helpers/helpers';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import withStyles from '@material-ui/core/styles/withStyles';
import sweetAlertStyle from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.jsx';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import {
  AddBoxTwoTone,
  CancelPresentation,
  DeleteForever,
  OpenInNew,
} from '@material-ui/icons';
import { NavLink } from 'react-router-dom';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import { put } from 'helpers/apiHelpers';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/styles/makeStyles';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { currencyToSymbol } from 'utils/currencies';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';

import PlannerCardItem from './PlannerCardItem';
import {
  carbohydratesConstant,
  proteinConstant,
  fatConstant,
  roughageConstant,
} from './utils';

const DishInfo = withStyles({
  body1: {
    fontSize: '0.8rem',
  },
})(Typography);

const IconButtonStyled = withStyles({
  root: {
    padding: '12px 0',
  },
})(IconButton);

const useStyles = makeStyles({
  ...buttonsStyle,
  ...extendedFormsStyle,
  ...sweetAlertStyle,
  card: {
    maxWidth: '350px',
    margin: '0 auto',
    position: 'relative',
    overflow: 'initial',
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0!important',
  },
  dishStatisticWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  dishStatistic: {
    maxWidth: '58%',
    minWidth: '160px',
    paddingLeft: '4px',
    paddingTop: '4px',
    flex: '1 auto',
  },
  dishInfoWrapper: {
    backgroundColor: '#50d3ee',
    padding: '4px 4px 0 4px',
    flex: '1 auto',
  },
  codeInputWrapper: {
    position: 'relative',
    borderTop: `1px solid ${grayColor[21]}`,
  },
  progressWrapper: {
    position: 'absolute',
    bottom: '10px',
    right: '0',
  },
  actionButtonWrapper: {
    width: '30px',
    maxWidth: '30px',
    backgroundColor: grayColor[19],
    padding: '4px 4px 0 4px',
    flex: '1 auto',
  },
  navLink: {
    color: '#747474',
    cursor: 'pointer',
    height: 24,
  },
});

const PlannerCard = ({
  statisticsFor,
  showDishStats,
  currency,
  menuPlanners,
  viewMode,
  canAssign,
  onAssign,
  onRemove,
  onMenuPlannerUpdated,
  selectedBrand,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const defaultCode = menuPlanners[0]?.reportCode || '';

  const [code, setCode] = useState(defaultCode);
  const [isSavingCode, setIsSavingCode] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    setCode(defaultCode);
  }, [defaultCode]);

  const toggleIsRemoving = () => setIsRemoving(prev => !prev);

  const calculateElementPercent = (element, elementConstant, statistics) =>
    (
      ((100 * elementConstant) / statistics.macrosAfterProcessing?.calories) *
        element || 0
    ).toFixed(2);

  const calculateElement = element =>
    (parseFloat(element ?? 0) || 0).toFixed(2);

  const updatePlanning = () => {
    const [menuPlanner] = menuPlanners;
    if (menuPlanner.reportCode !== code) {
      setIsSavingCode(true);

      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      put(menuPlanner['@id'], { reportCode: code }).then(() => {
        setIsSavingCode(false);
        onMenuPlannerUpdated();
      });
    }
  };

  const handleRemove = menuPlannerId => {
    toggleIsRemoving();
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    onRemove(menuPlannerId).then(() => {
      toggleIsRemoving();
    });
  };

  const draftView = viewMode === 'DRAFT';
  const menuPlanner = menuPlanners[0] || null;
  const allowedToWriteCode =
    menuPlanner &&
    ((draftView && menuPlanner.draft) || (!draftView && !menuPlanner.draft));
  const opacity = draftView && menuPlanner && !menuPlanner.draft ? '0.5' : '1';
  let statistics = { macrosAfterProcessing: {} };

  if (menuPlanner) {
    if (
      menuPlanner.sizes !== null &&
      typeof menuPlanner.sizes[statisticsFor] !== 'undefined'
    ) {
      statistics = menuPlanner.sizes[statisticsFor];

      if (statistics === null) {
        statistics = { macrosAfterProcessing: {} };
      }
    }
  }
  // eslint-disable-next-line no-unsafe-optional-chaining -- FIXME
  const { weightAfterWOM, weight } = statistics?.macrosAfterProcessing;
  const weightAfterWOMCalculated = calculateElement(weightAfterWOM);
  const weightCalculated = calculateElement(weight);

  const caloriesCalculated = calculateElement(
    statistics.macrosAfterProcessing.calories
  );

  const carbohydratesCalculated = calculateElement(
    statistics.macrosAfterProcessing.carbohydrates
  );

  const carbohydratesPercentage = calculateElementPercent(
    statistics.macrosAfterProcessing.carbohydrates,
    carbohydratesConstant,
    statistics
  );

  const carbohydratesCalculatedNet = calculateElement(
    Math.max(
      statistics.macrosAfterProcessing.carbohydrates -
        statistics.macrosAfterProcessing.roughage,
      0
    ).toFixed(2)
  );
  const carbohydratesPercentageNet = calculateElementPercent(
    Math.max(
      statistics.macrosAfterProcessing.carbohydrates -
        statistics.macrosAfterProcessing.roughage,
      0
    ).toFixed(2),
    carbohydratesConstant,
    statistics
  );

  const fatCalculated = calculateElement(statistics.macrosAfterProcessing.fat);

  const fatPercentage = calculateElementPercent(
    statistics.macrosAfterProcessing.fat,
    fatConstant,
    statistics
  );

  const glycemicIndexCalculated = calculateElement(
    statistics.macrosAfterProcessing.glycemicIndex
  );

  const proteinCalculated = calculateElement(
    statistics.macrosAfterProcessing.protein
  );

  const proteinPercentage = calculateElementPercent(
    statistics.macrosAfterProcessing.protein,
    proteinConstant,
    statistics
  );

  const roughageCalculated = (
    carbohydratesCalculated - carbohydratesCalculatedNet
  ).toFixed(2);

  const roughagePercentage = calculateElementPercent(
    statistics.macrosAfterProcessing.roughage,
    roughageConstant,
    statistics
  );

  return (
    <Card className={classes.card}>
      {menuPlanner ? (
        <>
          <CardContent
            className={classes.cardContent}
            style={{
              minWidth: menuPlanner && showDishStats ? '350px' : '250px',
            }}
          >
            <div className={classes.dishStatisticWrapper}>
              {showDishStats && (
                <div
                  className={classes.dishStatistic}
                  style={{
                    opacity,
                  }}
                >
                  <div>
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.calories')}
                      value={caloriesCalculated}
                      parameter="kcal"
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.protein')}
                      value={`${proteinCalculated}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )} (${proteinPercentage}%)`}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.carbohydrates')}
                      value={`${carbohydratesCalculated}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )}
                          (${carbohydratesPercentage}%)
                        `}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.carbohydratesNet')}
                      value={`${carbohydratesCalculatedNet}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )}
                          (${carbohydratesPercentageNet}%)`}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.roughage')}
                      value={`${roughageCalculated}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )} (${roughagePercentage})%`}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.fat')}
                      value={`${fatCalculated}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )}
                          (${fatPercentage})%
                   `}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.ig')}
                      value={glycemicIndexCalculated}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.productWeight')}
                      value={`${weightAfterWOMCalculated}${t(
                        'menuPlanner.plannerCardSummary.g'
                      )}
                    (${weightCalculated})${t(
                        'menuPlanner.plannerCardSummary.g'
                      )}`}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.price')}
                      value={calculateElement(statistics.price)}
                      parameter={currencyToSymbol(currency)}
                    />
                    <PlannerCardItem
                      title={t('menuPlanner.plannerCard.new.rate')}
                      value={
                        <>
                          {statistics.rateCount > 0
                            ? (statistics.rate / statistics.rateCount).toFixed(
                                2
                              )
                            : '-'}
                        </>
                      }
                    />
                  </div>
                </div>
              )}
              <div
                className={classes.dishInfoWrapper}
                style={{
                  opacity,
                }}
              >
                <DishInfo component="p">
                  {`(${menuPlanner.dish.id}) ${menuPlanner.dish.nameForClient}`}
                </DishInfo>
                {allowedToWriteCode && (
                  <div className={classes.codeInputWrapper}>
                    <FormTextInput
                      classes={classes}
                      name={`menu_planning_${menuPlanner.id}`}
                      label={t('menuPlanner.plannerCard.code')}
                      value={code}
                      handleChange={e => {
                        setCode(e.target.value.replace(/\s/g, ''));
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && code.length > 0) {
                          updatePlanning();
                        }
                      }}
                      onBlur={updatePlanning}
                    />
                    <div className={classes.progressWrapper}>
                      {isSavingCode && <CircularProgress />}
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.actionButtonWrapper}>
                {draftView && (
                  <IconButtonStyled disabled={!canAssign} onClick={onAssign}>
                    <AddBoxTwoTone />
                  </IconButtonStyled>
                )}
                {menuPlanner && (
                  <>
                    {draftView && menuPlanner.draft && (
                      <IconButtonStyled
                        onClick={() => handleRemove(menuPlanner['@id'])}
                        disabled={isRemoving}
                      >
                        <DeleteForever />
                      </IconButtonStyled>
                    )}
                    <IconButtonStyled aria-label="Share">
                      <NavLink
                        target="_blank"
                        to={`/admin/meals/edit/${menuPlanner.dish.id}?selectedBrand=${selectedBrand}`}
                        // style={DISH_LINK_STYLES}
                        className={classes.navLink}
                      >
                        <OpenInNew />
                      </NavLink>
                    </IconButtonStyled>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </>
      ) : (
        <CardContent className={classes.cardContent}>
          {draftView ? (
            <IconButtonStyled disabled={!canAssign} onClick={onAssign}>
              <AddBoxTwoTone />
            </IconButtonStyled>
          ) : (
            <IconButtonStyled disabled={true}>
              <CancelPresentation />
            </IconButtonStyled>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const combinedStyles = combineStyles(
  buttonsStyle,
  extendedFormsStyle,
  sweetAlertStyle
);

const mapStateToProps = state => ({
  currency: state.Brands.brand.multinational.currency,
  selectedBrand: state.Auth.selectedBrand,
});

const enhance = compose(connect(mapStateToProps), withStyles(combinedStyles));

export default enhance(PlannerCard);
